import { motion } from 'framer-motion'
import { Users, Lock, Play, LogOut, Crown, Settings, Check, X } from 'lucide-react'
import { useSocket } from '../hooks/useSocket'
import { useAuthStore } from '../hooks/useAuthStore'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

const GameCard = ({ game, isCurrentGame = false }) => {
  const { t } = useTranslation('lobby')
  const { joinGame, leaveGame, startGame, toggleReady, readyPlayers, socket, availableGameTypes } = useSocket()
  const { user } = useAuthStore()
  const [password, setPassword] = useState('')
  const [showPasswordInput, setShowPasswordInput] = useState(false)

  const isHost = user && game.host === user.id
  const isInGame = game.players && game.players.includes(user?.id)
  const isFull = game.players && game.players.length >= game.maxPlayers
  const isReady = readyPlayers.includes(user?.id)
  const allPlayersReady = game.players && readyPlayers.length === game.players.length && game.players.length >= 2

  const gameTypeInfo = availableGameTypes.find(g => g.name === game.type)
  const isPremiumLocked = Boolean(user?.isAnonymous) && gameTypeInfo?.tier === 'premium' && !isInGame

  const getGameCategoryColor = (category) => {
    switch (category) {
      case 'strategy': return 'bg-blue-600'
      case 'puzzle': return 'bg-green-600'
      case 'action': return 'bg-red-600'
      case 'casual': return 'bg-yellow-600'
      case 'educational': return 'bg-purple-600'
      default: return 'bg-gray-600'
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'waiting': return 'text-green-400'
      case 'in-progress': return 'text-yellow-400'
      case 'finished': return 'text-gray-400'
      default: return 'text-gray-400'
    }
  }

  const handleJoinGame = () => {
    if (isPremiumLocked) return
    if (game.hasPassword && !showPasswordInput) {
      setShowPasswordInput(true)
      return
    }
    joinGame(game.id, password)
    setShowPasswordInput(false)
    setPassword('')
  }

  const handleStartGame = () => {
    if (isHost) {
      startGame(game.id)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-effect rounded-xl p-6 hover:bg-white/5 transition-colors"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-semibold text-white">{game.name}</h3>
            {game.hasPassword && <Lock className="w-4 h-4 text-yellow-400" />}
            {isHost && <Crown className="w-4 h-4 text-yellow-400" title={t('gameCard.host')} />}
          </div>
          
          <div className="flex items-center gap-2 mb-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${getGameCategoryColor(gameTypeInfo?.category)}`}>
              {gameTypeInfo?.displayName || game.type}
            </span>
            <span className={`text-sm font-medium ${getStatusColor(game.status)}`}>
              {game.status.charAt(0).toUpperCase() + game.status.slice(1)}
            </span>
            {gameTypeInfo?.tier === 'premium' && (
              <span className="px-2 py-1 rounded-full text-xs font-medium text-white bg-purple-600 flex items-center gap-1">
                <Lock className="w-3 h-3" />
                {t('gameCard.premiumBadge')}
              </span>
            )}
          </div>
        </div>

        <div className="text-right">
          <div className="flex items-center gap-1 text-gray-300">
            <Users className="w-4 h-4" />
            <span className="text-sm">
              {game.players ? game.players.length : 0}/{game.maxPlayers}
            </span>
          </div>
        </div>
      </div>

      {/* Game Settings Preview */}
      {game.settings && Object.keys(game.settings).length > 0 && (
        <div className="mb-4 p-3 bg-gray-800/50 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Settings className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-400">{t('gameCard.settings')}</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {Object.entries(game.settings).map(([key, value]) => (
              <div key={key} className="flex justify-between">
                <span className="text-gray-400 capitalize">
                  {key.replace(/([A-Z])/g, ' $1')}:
                </span>
                <span className="text-white">{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Password Input */}
      {showPasswordInput && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mb-4"
        >
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('gameCard.enterPassword')}
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            onKeyPress={(e) => e.key === 'Enter' && handleJoinGame()}
          />
        </motion.div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        {isCurrentGame ? (
          <>
            {game.status === 'waiting' && (
              <button
                onClick={() => toggleReady(game.id)}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm font-medium ${
                  isReady 
                    ? 'bg-green-600 hover:bg-green-700 text-white' 
                    : 'bg-gray-600 hover:bg-gray-700 text-white'
                }`}
                title={isReady ? 'Click to mark as not ready' : 'Click to mark as ready'}
              >
                {isReady ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                {isReady ? t('gameCard.ready') : t('gameCard.notReady')}
              </button>
            )}
            {isHost && game.status === 'waiting' && (
              <button
                onClick={handleStartGame}
                disabled={!allPlayersReady}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm font-medium ${
                  allPlayersReady 
                    ? 'bg-green-600 hover:bg-green-700 text-white' 
                    : 'bg-gray-600 cursor-not-allowed text-gray-300'
                }`}
                title={allPlayersReady ? t('gameCard.startGame') : t('gameCard.waitingForPlayers')}
              >
                <Play className="w-4 h-4" />
                {allPlayersReady ? t('gameCard.startGame') : t('gameCard.waiting')}
              </button>
            )}
            <button
              onClick={leaveGame}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <LogOut className="w-4 h-4" />
              {t('gameCard.leave')}
            </button>
          </>
        ) : (
          <button
            onClick={handleJoinGame}
            disabled={isFull || game.status !== 'waiting' || isPremiumLocked}
            title={isPremiumLocked ? t('gameCard.premiumLocked') : undefined}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
          >
            {isPremiumLocked ? <Lock className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {isPremiumLocked
              ? t('gameCard.premiumLocked')
              : isFull ? t('gameCard.full') : game.status !== 'waiting' ? t('gameCard.inProgress') : t('gameCard.joinGame')}
          </button>
        )}
      </div>

      {/* Players List (for current game) */}
      {isCurrentGame && game.players && (
        <div className="mt-4 pt-4 border-t border-gray-700">
          <h4 className="text-sm font-medium text-gray-300 mb-2">
            {t('statistics.onlinePlayers')} ({t('gameCard.playersReady', { ready: readyPlayers.length, total: game.players.length })})
          </h4>
          <div className="space-y-2">
            {game.players.map((player) => {
              const playerId = player.id || player;
              const playerIsReady = readyPlayers.includes(playerId)
              return (
                <div key={playerId} className="flex items-center gap-3 p-2 bg-gray-700/30 rounded-lg">
                  <div className={`w-3 h-3 rounded-full ${playerIsReady ? 'bg-green-500' : 'bg-gray-500'}`}></div>
                  <div className="flex-1">
                    <span className="text-gray-300 text-sm">
                      {player.username || playerId}
                      {(player.id || playerId) === user?.id && (
                        <span className="text-blue-400 ml-1">{t('gameCard.you')}</span>
                      )}
                      {(player.id || playerId) === game.host && (
                        <Crown className="w-3 h-3 text-yellow-400 inline ml-1" />
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    {playerIsReady ? (
                      <div className="flex items-center gap-1 text-green-400">
                        <Check className="w-3 h-3" />
                        <span className="text-xs">{t('gameCard.ready')}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-gray-500">
                        <X className="w-3 h-3" />
                        <span className="text-xs">{t('gameCard.notReady')}</span>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </motion.div>
  )
}

export default GameCard